/**
 * Cierre de estados de pago.
 *
 * Hasta ahora el EP se calculaba al vuelo desde un rango de fechas y no
 * quedaba nada guardado. Eso deja tres agujeros:
 *
 *  - Nada marca una guía como ya cobrada, así que una guía corregida o cargada
 *    tarde podía entrar dos veces, o quedar fuera para siempre.
 *  - El «acumulado anterior» de cada partida no tenía de dónde salir: iba
 *    siempre en cero, y el acumulado del contrato había que escribirlo a mano.
 *  - Los precios no quedaban congelados, así que un reajuste movía los EP ya
 *    presentados.
 *
 * Cerrar un EP guarda el documento completo —líneas, precios del día, totales,
 * UF de cierre— y marca sus guías. A partir de ahí ese EP no se recalcula: se
 * muestra lo que se emitió.
 *
 * El período es libre, de fecha a fecha. No se asume mes calendario porque la
 * fecha de corte la define quien administra el contrato.
 *
 * Reabrir es una operación normal, no un castigo: pide un motivo, lo deja
 * registrado y devuelve las guías a su estado anterior.
 */

import { COL } from './firebase';
import { guardar, leerDoc, listar, marcarGuias } from './repositorio';
import { metaNueva, uuidv7 } from './tiposDatos';
import type { EstadoEP, LineaEP, TotalesEP } from '@/domain/tipos';

/**
 * Una cantidad cargada a mano sobre una partida.
 *
 * `fijo` marca los que se repiten todos los períodos —el arriendo de radios,
 * los operadores de apoyo— para que el EP siguiente los proponga solo en vez
 * de que haya que acordarse. Se pueden cambiar o quitar en cualquier EP.
 */
export interface AjusteEP {
  cantidad: number;
  motivo: string;
  fijo?: boolean;
}

export interface MovimientoEP {
  accion: 'CERRADO' | 'REABIERTO';
  en: string;
  por: string | null;
  motivo: string | null;
}

export interface DocEstadoPago {
  id: string;
  numero: number;
  periodoDesde: string;
  periodoHasta: string;
  valorUf: number;
  factorGastosGenerales: number;
  estado: EstadoEP;
  /** Congelado al cerrar: precios, cantidades y acumulados de ese momento. */
  lineas: LineaEP[];
  totales: TotalesEP;
  /** Qué guías entraron. Es lo que permite deshacer el cierre. */
  guiaIds: string[];
  /**
   * Cantidades cargadas a mano, por código de partida.
   *
   * No todo pasa por el libro de guías: las cuotas mensuales de radios y
   * operadores de apoyo, y las correcciones de un período. Quedan guardadas
   * con su motivo dentro del EP, así que un EP cerrado conserva de dónde salió
   * cada número.
   */
  ajustes: Record<string, AjusteEP>;
  cerradoEn: string | null;
  cerradoPor: string | null;
  historial: MovimientoEP[];
}

/** Acumulado físico y financiero por código de partida. */
export type Acumulado = Record<string, { cantidad: number; montoUf: number }>;

// ── Lectura ───────────────────────────────────────────────────────────────

/** Todos los estados de pago, del más reciente al más antiguo. */
export async function listarEstadosPago(): Promise<DocEstadoPago[]> {
  const eps = await listar<DocEstadoPago>(COL.estadosPago);
  return eps.sort((a, b) => b.periodoHasta.localeCompare(a.periodoHasta));
}

export async function leerEstadoPago(id: string): Promise<DocEstadoPago | null> {
  return leerDoc<DocEstadoPago>(COL.estadosPago, id);
}

/**
 * El estado de pago que lleva ese número.
 *
 * Se identifica por número y no por período. Antes se buscaba por las fechas
 * exactas, y eso dejaba sin arreglo un EP cerrado con el período equivocado:
 * al corregir las fechas la app dejaba de reconocerlo, y al intentar cerrarlo
 * de nuevo se quejaba de que el número ya existía. Un EP es su número; las
 * fechas son un dato suyo, y los datos se corrigen.
 */
export function epDelNumero(eps: DocEstadoPago[], numero: number): DocEstadoPago | null {
  if (!numero) return null;
  return eps.find((e) => e.numero === numero) ?? null;
}

/**
 * Acumulado al cierre del último EP anterior a `desde`.
 *
 * Es la cadena entre estados de pago: lo que el Excel arrastraba de la columna
 * «acumulado anterior» y aquí sale del EP previo, sin transcribir.
 */
export function acumuladoAntesDe(eps: DocEstadoPago[], desde: string): Acumulado {
  const previos = eps
    .filter((e) => e.estado !== 'ABIERTO' && e.periodoHasta < desde)
    .sort((a, b) => a.periodoHasta.localeCompare(b.periodoHasta));

  const ultimo = previos.at(-1);
  if (!ultimo) return {};

  const acc: Acumulado = {};
  for (const l of ultimo.lineas) {
    acc[l.itemCodigo] = { cantidad: l.cantAcumFecha, montoUf: l.montoAcumFechaUf };
  }
  return acc;
}

/**
 * Los ajustes fijos del último EP cerrado, para proponerlos en el siguiente.
 *
 * Un cobro que se repite todos los meses no debería depender de que alguien se
 * acuerde de escribirlo.
 */
export function ajustesFijosPrevios(
  eps: DocEstadoPago[],
  /** Sólo se miran los EP anteriores a éste. */
  antesDe?: number,
): Record<string, AjusteEP> {
  const cerrados = eps
    .filter((e) => e.estado !== 'ABIERTO' && (antesDe == null || e.numero < antesDe))
    .sort((a, b) => a.numero - b.numero);
  const ultimo = cerrados.at(-1);
  if (!ultimo) return {};

  const fijos: Record<string, AjusteEP> = {};
  for (const [codigo, a] of Object.entries(ultimo.ajustes ?? {})) {
    if (a.fijo) fijos[codigo] = { ...a };
  }
  return fijos;
}

/**
 * Primer día que la app ha facturado alguna vez.
 *
 * Acota hasta dónde se puede ir a buscar guías atrasadas: sólo dentro de
 * tramos que ya pasaron por un estado de pago. Más atrás está el libro
 * histórico, que nunca se cobró desde acá.
 */
export function inicioHistoria(eps: DocEstadoPago[]): string | undefined {
  const cerrados = eps.filter((e) => e.estado !== 'ABIERTO');
  if (cerrados.length === 0) return undefined;
  return cerrados.reduce(
    (min, e) => (e.periodoDesde < min ? e.periodoDesde : min),
    cerrados[0]!.periodoDesde,
  );
}

/** Suma de lo cerrado, para el acumulado del contrato. */
export function totalCerradoUf(eps: DocEstadoPago[]): number {
  return eps
    .filter((e) => e.estado !== 'ABIERTO')
    .reduce((s, e) => s + (e.totales?.totalUf ?? 0), 0);
}

// ── Cierre ────────────────────────────────────────────────────────────────

export interface DatosCierre {
  numero: number;
  periodoDesde: string;
  periodoHasta: string;
  valorUf: number;
  factorGastosGenerales: number;
  lineas: LineaEP[];
  totales: TotalesEP;
  guiaIds: string[];
  ajustes: Record<string, AjusteEP>;
}

/**
 * Motivos por los que un cierre no debe seguir adelante.
 * Se devuelven todos juntos para no corregir de a uno.
 */
export function problemasParaCerrar(
  d: Pick<DatosCierre, 'numero' | 'periodoDesde' | 'periodoHasta' | 'guiaIds'> & {
    ajustes?: DocEstadoPago['ajustes'];
  },
  eps: DocEstadoPago[],
  guiasYaFacturadas: { id: string; estadoPagoId: string | null }[],
  /** El EP que se está volviendo a cerrar: no compite consigo mismo. */
  excluirId?: string | null,
): string[] {
  const otros = eps.filter((e) => e.id !== excluirId);
  const p: string[] = [];

  if (!d.numero || d.numero <= 0) p.push('Falta el número de EP.');
  if (d.periodoHasta < d.periodoDesde) p.push('El período termina antes de empezar.');
  if (d.guiaIds.length === 0 && Object.keys(d.ajustes ?? {}).length === 0) {
    p.push('No hay guías ni cantidades cargadas en el período: no hay nada que cerrar.');
  }

  const repetido = otros.find((e) => e.numero === d.numero);
  if (repetido) {
    p.push(
      `El EP N° ${d.numero} ya existe, del ${repetido.periodoDesde} al ${repetido.periodoHasta}.`,
    );
  }

  // Un EP abierto es un borrador: solto sus guías, así que no reserva período.
  const solapado = otros.find(
    (e) =>
      e.estado !== 'ABIERTO' &&
      e.periodoDesde <= d.periodoHasta &&
      e.periodoHasta >= d.periodoDesde,
  );
  if (solapado) {
    p.push(
      `El período se cruza con el EP N° ${solapado.numero} ` +
        `(${solapado.periodoDesde} a ${solapado.periodoHasta}).`,
    );
  }

  const ajenas = guiasYaFacturadas.filter((g) => g.estadoPagoId);
  if (ajenas.length > 0) {
    p.push(
      `${ajenas.length} guía${ajenas.length === 1 ? '' : 's'} del período ya está` +
        `${ajenas.length === 1 ? '' : 'n'} en otro estado de pago.`,
    );
  }

  return p;
}

/**
 * Cierra el EP: guarda el documento y marca sus guías como facturadas.
 *
 * El documento se escribe antes que las guías. Si algo falla al marcarlas, el
 * EP queda cerrado y las guías sin marcar — visible y corregible reabriendo,
 * que es mejor que guías marcadas apuntando a un EP que no existe.
 */
export async function cerrarEstadoPago(
  d: DatosCierre,
  usuarioId: string | null,
  /** Al volver a cerrar uno reabierto, se conserva su documento e historial. */
  anterior?: DocEstadoPago | null,
): Promise<DocEstadoPago> {
  const ahora = new Date().toISOString();
  const ep: DocEstadoPago = {
    ...metaNueva(usuarioId),
    ...(anterior ?? {}),
    id: anterior?.id ?? uuidv7(),
    numero: d.numero,
    periodoDesde: d.periodoDesde,
    periodoHasta: d.periodoHasta,
    valorUf: d.valorUf,
    factorGastosGenerales: d.factorGastosGenerales,
    estado: 'CERRADO',
    lineas: d.lineas,
    totales: d.totales,
    guiaIds: d.guiaIds,
    ajustes: d.ajustes ?? {},
    cerradoEn: ahora,
    cerradoPor: usuarioId,
    historial: [
      ...(anterior?.historial ?? []),
      { accion: 'CERRADO', en: ahora, por: usuarioId, motivo: null },
    ],
    actualizadoEn: ahora,
    actualizadoPor: usuarioId,
  } as DocEstadoPago;

  await guardar(COL.estadosPago, ep);
  await marcarGuias(d.guiaIds, { estado: 'FACTURADA', estadoPagoId: ep.id }, usuarioId);
  return ep;
}

/**
 * Reabre un EP cerrado y devuelve sus guías a «EMITIDA».
 *
 * Queda el motivo y quién lo hizo. El EP no se borra: se reabre, se corrige y
 * se vuelve a cerrar con el mismo número.
 */
export async function reabrirEstadoPago(
  ep: DocEstadoPago,
  motivo: string,
  usuarioId: string | null,
): Promise<DocEstadoPago> {
  const ahora = new Date().toISOString();
  const abierto: DocEstadoPago = {
    ...ep,
    estado: 'ABIERTO',
    cerradoEn: null,
    cerradoPor: null,
    historial: [
      ...(ep.historial ?? []),
      { accion: 'REABIERTO', en: ahora, por: usuarioId, motivo },
    ],
    actualizadoEn: ahora,
    actualizadoPor: usuarioId,
  } as DocEstadoPago;

  await marcarGuias(ep.guiaIds, { estado: 'EMITIDA', estadoPagoId: null }, usuarioId);
  await guardar(COL.estadosPago, abierto);
  return abierto;
}
